import { Radar, ArrowUpRight, Eye, MessageCircle, Quote, ChevronRight } from "lucide-react";
import type { ParentPriority } from "@/lib/parent/currentPriority";
import type { ParentAnalyticsResult } from "@/hooks/useParentAnalytics";
import { EvidenceDrilldown } from "@/components/parent/hub/ParentDrilldownSheet";

interface Props {
  priority: ParentPriority;
  displayName: string;
  analytics: ParentAnalyticsResult;
}

/**
 * Primary Signal Panel — single, visually dominant interpretation of the
 * most important current data point. Data-first: signal → evidence → watch → ask.
 * Sits just under the Data Command Strip.
 */
export default function PrimarySignalPanel({ priority, displayName, analytics }: Props) {
  const tonePill = priority.tone === "watch"
    ? "border-amber-400/30 bg-amber-500/10 text-amber-200"
    : priority.tone === "ready"
      ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-300"
      : "border-cyan-400/30 bg-cyan-500/10 text-cyan-300";
  const headline = priority.concept === "general"
    ? `${displayName}'s learning signal is forming`
    : `${priority.label} is the current focus`;

  return (
    <section
      className="relative overflow-hidden rounded-[28px] border border-white/[0.08] ring-1 ring-inset ring-white/[0.04] bg-[linear-gradient(180deg,hsl(var(--card)/0.78)_0%,hsl(var(--card)/0.5)_100%)] p-6 backdrop-blur-2xl shadow-[0_44px_100px_-50px_rgba(0,0,0,0.75),0_4px_14px_-6px_rgba(0,0,0,0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.08)] sm:p-7"
      aria-label="Primary learning signal"
    >
      <div className="pointer-events-none absolute -top-32 right-1/3 h-60 w-60 rounded-full bg-cyan-500/[0.07] blur-[100px]" />
      <div className="pointer-events-none absolute -bottom-24 -left-12 h-48 w-48 rounded-full bg-violet-500/[0.05] blur-[90px]" />
      <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-400/30 shadow-[inset_0_1px_0_0_hsl(var(--foreground)/0.12)]">
              <Radar className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.28em] text-cyan-300/85">Primary Learning Signal</p>
              <p className="text-[12px] text-muted-foreground">Most important read on {displayName} right now</p>
            </div>
          </div>
          <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium backdrop-blur ${tonePill}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
            {priority.betaIndex
              ? priority.betaIndex === 13
                ? "Final checkpoint"
                : `Current focus · ${priority.betaIndex} of 13`
              : priority.statusChip}
          </span>
        </div>

        <h2 className="mt-7 font-heading text-[26px] font-semibold tracking-tight text-foreground sm:text-[32px] leading-[1.12]">
          {headline}
        </h2>
        <p className="mt-2 text-[13px] text-muted-foreground/90">
          {priority.moduleLabel}{priority.concept !== "general" ? " · learning path" : ""}
        </p>

        {/* Connected signal rail — Evidence | Watch | Ask Tonight.
            Two segments + a featured prompt; soft dividers, no nested boxes. */}
        <div className="mt-7 grid gap-px overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] sm:grid-cols-2">
          <EvidenceDrilldown
            priority={priority}
            analytics={analytics}
            trigger={
              <button type="button" className="text-left transition hover:bg-card/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/40">
                <SignalSegment icon={ArrowUpRight} eyebrow="Evidence · view stats" body={priority.evidence} accent="primary" interactive />
              </button>
            }
          />
          <SignalSegment icon={Eye} eyebrow="Watch" body={priority.watch} accent="amber" />
        </div>

        {/* Featured Ask Tonight prompt — premium quote treatment. */}
        <div className="mt-3 relative overflow-hidden rounded-2xl border border-violet-400/20 bg-[linear-gradient(135deg,rgba(167,139,250,0.08)_0%,hsl(var(--card)/0.5)_60%,rgba(34,211,238,0.05)_100%)] p-5 shadow-[inset_0_1px_0_0_hsl(var(--foreground)/0.06)]">
          <div className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-violet-500/[0.08] blur-[60px]" />
          <div className="relative flex items-start gap-3.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-200 ring-1 ring-violet-400/30">
              <MessageCircle className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.22em] text-violet-200/90">Ask tonight</p>
              <p className="mt-2 font-heading text-[17px] leading-[1.35] text-foreground sm:text-[19px]">
                <Quote className="mr-1.5 inline h-3.5 w-3.5 -translate-y-1 text-violet-300/60" aria-hidden />
                {priority.prompt.text}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SignalSegment({
  icon: Icon,
  eyebrow,
  body,
  accent,
  interactive = false,
}: {
  icon: React.ComponentType<{ className?: string }>;
  eyebrow: string;
  body: string;
  accent: "primary" | "amber";
  interactive?: boolean;
}) {
  const tone =
    accent === "amber"
      ? "text-amber-200 bg-amber-500/10 ring-amber-400/25"
      : "text-cyan-300 bg-cyan-500/10 ring-cyan-400/25";
  return (
    <div className="relative bg-card/40 p-5 backdrop-blur-md">
      <div className="flex items-center gap-2.5">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ring-1 ${tone}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <p className="text-[10.5px] font-medium uppercase tracking-[0.2em] text-muted-foreground/85">{eyebrow}</p>
        {interactive && <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground/70" />}
      </div>
      <p className="mt-2.5 text-[13.5px] leading-relaxed text-foreground/95">{body}</p>
    </div>
  );
}
